import React from 'react';
import { Lightbulb, Plus, MessageCircle, ShoppingCart, Info, User, Sparkles, ExternalLink } from 'lucide-react';

export default function FamilyRecommendations({ recommendations = [], isLoading = false, onRecommendationAction }) {

  const handleAddToList = async (recommendationId, bookData) => {
    if (onRecommendationAction) {
      await onRecommendationAction(recommendationId, 'add_to_list', bookData);
    }
  };

  const handleAddToShopping = async (book) => {
    if (onRecommendationAction) {
      await onRecommendationAction(book.id, 'add_to_shopping', book);
    }
  };

  const handleAskRecommender = async (recommenderId, bookTitle, recommendationId) => {
    if (onRecommendationAction) {
      await onRecommendationAction(recommendationId, 'ask_recommender', {
        recommended_by_id: recommenderId,
        book_title: bookTitle
      });
    }
  };

  const handleViewOnAmazon = (book) => {
    const searchQuery = encodeURIComponent(`${book.book_title || book.title} ${book.book_author || book.author}`);
    const amazonUrl = `https://www.amazon.com/s?k=${searchQuery}&i=stripbooks`;
    window.open(amazonUrl, '_blank');
  };

  const getBookInitials = (title) => {
    if (!title || typeof title !== 'string') {
      return '?';
    }
    return title
      .split(' ')
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  };

  const getBookCover = (rec) => {
    // Check for cover URL in different possible fields
    const coverUrl = rec.cover_url || rec.link_metadata?.image || rec.image_url;
    
    if (coverUrl) {
      return (
        <img 
          src={coverUrl} 
          alt={rec.book_title || 'Book cover'}
          className="w-full h-full object-cover rounded-lg"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    
    return null;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-azure-200 shadow-sm border-l-4 border-l-azure-500">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-5 h-5 text-azure-600" />
          <h2 className="text-lg font-semibold text-slate-900">Family Book Suggestions</h2>
        </div>
        
        <div className="text-center py-8 text-stone-500">
          <Lightbulb className="w-12 h-12 mx-auto mb-4 text-stone-300 animate-pulse" />
          <p>Loading recommendations...</p>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-azure-200 shadow-sm border-l-4 border-l-azure-500">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-5 h-5 text-azure-600" />
          <h2 className="text-lg font-semibold text-slate-900">Family Book Suggestions</h2>
        </div>
        
        <div className="text-center py-8">
          <Lightbulb className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No New Suggestions</h3>
          <p className="text-stone-600">Check back soon for new book recommendations from your family!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-5 border border-azure-200 shadow-sm border-l-4 border-l-azure-500">
      <div className="flex items-center gap-2 mb-4">
        <Lightbulb className="w-5 h-5 text-azure-600" />
        <h2 className="text-lg font-semibold text-slate-900">Family Book Suggestions</h2>
      </div>

      <div className="space-y-3">
        {recommendations.map((rec) => (
          <div key={rec.id} className="bg-azure-50 rounded-xl p-4 flex gap-3 items-center">
            {/* Book Cover */}
            <div className="w-10 h-13 flex-shrink-0 relative overflow-hidden rounded-lg">
              {getBookCover(rec)}
              {/* Fallback to initials */}
              <div className="w-full h-full bg-gradient-to-br from-azure-500 to-azure-600 rounded-lg flex items-center justify-center text-white text-sm font-bold absolute inset-0">
                {getBookInitials(rec.book_title)}
              </div>
            </div>

            {/* Book Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 text-sm mb-1 truncate">
                {rec.book_title || 'Unknown Title'}
              </h3>
              <p className="text-xs text-azure-600 mb-2">by {rec.book_author || 'Unknown Author'}</p>
              
              {/* Source Badge */}
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-azure-100 rounded-full text-xs text-azure-700 font-medium">
                {rec.source === 'family_member' ? (
                  <>
                    <User className="w-3 h-3" />
                    {rec.recommended_by_name} recommended
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3 h-3" />
                    Family library suggestion
                  </>
                )}
              </div>

              {/* Recommendation Reason */}
              {rec.recommendation_reason && (
                <p className="text-xs text-azure-700 mt-2 line-clamp-2">
                  "{rec.recommendation_reason}"
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 flex-shrink-0">
              <button
                onClick={() => handleAddToList(rec.id, rec)}
                className="flex items-center gap-1 px-3 py-1.5 bg-azure-500 text-white text-xs font-medium rounded-lg hover:bg-azure-600 active:scale-95 transition-all"
              >
                <Plus className="w-3 h-3" />
                Add to List
              </button>

              <button
                onClick={() => handleViewOnAmazon(rec)}
                className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white text-xs font-medium rounded-lg hover:bg-orange-600 active:scale-95 transition-all"
              >
                <ExternalLink className="w-3 h-3" />
                Amazon
              </button>

              {rec.source === 'family_member' ? (
                <button
                  onClick={() => handleAskRecommender(
                    rec.recommended_by_id, 
                    rec.book_title, 
                    rec.id
                  )}
                  className="flex items-center gap-1 px-3 py-1.5 bg-white text-azure-600 text-xs font-medium rounded-lg border border-azure-300 hover:bg-azure-50 active:scale-95 transition-all"
                >
                  <MessageCircle className="w-3 h-3" />
                  Ask {rec.recommended_by_name}
                </button>
              ) : (
                <button
                  onClick={() => handleAddToShopping(rec)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-white text-azure-600 text-xs font-medium rounded-lg border border-azure-300 hover:bg-azure-50 active:scale-95 transition-all"
                >
                  <ShoppingCart className="w-3 h-3" />
                  Add to Shopping
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* View More Button */}
      <div className="mt-4 text-center">
        <button className="text-sm text-azure-600 hover:text-azure-700 font-medium flex items-center gap-1 mx-auto">
          <Info className="w-4 h-4" />
          View All Family Recommendations
        </button>
      </div>
    </div>
  );
}

